import type { Aggregation, AggregationFn, MetricsQueryRequest } from '@/shared/types/metricsQuery';

import rawCatalog from './metricsCatalog.json';

// Re-exported from the shared request types so the catalog and the query layer
// can never drift: an aggregation function admitted by one is admitted by both.
export type { Aggregation, AggregationFn } from '@/shared/types/metricsQuery';

type Category = { id: string; label: string };

type AggregationMeta = {
  label: string;
  isDefault?: boolean;
  function: AggregationFn;
  allowedOn: ('raw' | 'cagg')[];
};

type GroupByOption = { label: string; column: string };

export type FilterType = 'uuid' | 'string' | 'boolean' | 'number';

type FilterOption = { label: string; column: string; type: FilterType };

export type DerivedKind = 'ratio' | 'ratio_complement';

// `percent`           → ratio displayed as `45%` (rounded integer).
// `score_5`           → ratio displayed as `4.3/5` (one-decimal, value passed through raw).
// `duration_seconds`  → ratio is interpreted as seconds and displayed via
//                       formatDurationFromSeconds (`45s`, `1m 30s`).
export type DerivedFormat = 'percent' | 'score_5' | 'duration_seconds';

export type DerivedMetric = {
  kind: DerivedKind;
  numerator: string;
  denominator: string;
  format: DerivedFormat;
};

export type CatalogMetric = {
  id: string;
  label: string;
  category: string;
  table: string;
  aggregations: Aggregation[];
  groupBy: string[];
  filters: string[];
  derived?: DerivedMetric;
  // Dimension that defines this metric's identity when its (table, function,
  // column) tuple collides with another metric. e.g. ai_csat_brain and
  // ai_conversations both query COUNT(DISTINCT session_id) on
  // ai_agents_conversations_daily — only groupBy=[csat_brain] differentiates
  // them. Used to disambiguate on lookup and to seed groupBy when the user
  // switches into this metric.
  primaryDimension?: string;
  // Header text for the value column in the table view. Set on histogram-style
  // metrics (e.g. ai_goal_achieved) where the metric `label` describes the
  // dimension being segmented and would collide with the groupBy column
  // header. When unset, callers fall back to `label`.
  valueLabel?: string;
  // OData clause that pins the metric's identity on shared CAGGs — the five
  // custom-insight metrics all sit on ai_agents_custom_insights_daily with
  // SUM(conv_count) and groupBy=[bucket_label]; only this filter (category +
  // insight_name) tells them apart. Merged into the saved query so the chart
  // reflects the chosen insight instead of the whole CAGG.
  defaultFilter?: string;
  insightCategory?: string;
};

export type TableFamily = 'raw' | 'cagg';

export type TableMeta = { type: TableFamily; timeColumn: string };

type Catalog = {
  categories: Category[];
  aggregations: Record<AggregationFn, AggregationMeta>;
  groupByOptions: Record<string, GroupByOption>;
  filterOptions: Record<string, FilterOption>;
  metrics: CatalogMetric[];
  tables: Record<string, TableMeta>;
};

const catalog = rawCatalog as unknown as Catalog;

// Runtime overlay for account-specific metrics that aren't known at build time.
// Today this is the catalog of custom insights — discovered by querying the
// ai_agents_custom_insights_daily CAGG for distinct (insight_name, category)
// tuples. Registered via registerDynamicMetrics from useCustomInsightsCatalog.
const dynamicMetrics = new Map<string, CatalogMetric>();
let dynamicMetricsVersion = 0;
const dynamicSubscribers = new Set<() => void>();

export const registerDynamicMetrics = (metrics: CatalogMetric[]): void => {
  dynamicMetrics.clear();
  metrics.forEach((m) => dynamicMetrics.set(m.id, m));
  dynamicMetricsVersion += 1;
  dynamicSubscribers.forEach((cb) => cb());
};

export const subscribeDynamicMetrics = (cb: () => void): (() => void) => {
  dynamicSubscribers.add(cb);
  return () => {
    dynamicSubscribers.delete(cb);
  };
};

export const getDynamicMetricsVersion = (): number => dynamicMetricsVersion;

// Each of the five custom-insight metrics used to be a hard-coded catalog entry
// keyed by a synthetic `custom_*` id. Those entries are now minted at runtime
// from the API, keyed by the raw `insight_name`. Reports saved against the old
// ids must still resolve, so we map them on read. Re-saving a legacy report
// rewrites metricId to the new id, self-healing the row.
const LEGACY_CUSTOM_INSIGHT_IDS: Record<string, string> = {
  custom_promise_to_pay: 'Promise_to_pay',
  custom_reason_for_missing_payment: 'Reason_for_missing_payment',
  custom_debtor_address: 'Debtor_address',
  custom_debt_amount: 'Debt_amount',
  custom_promised_payment_date: 'Promised_payment_date',
};

const getAllMetrics = (): CatalogMetric[] => [...catalog.metrics, ...dynamicMetrics.values()];

export const getCategories = (): Category[] => catalog.categories;

export const getMetrics = (): CatalogMetric[] => getAllMetrics();

export const getMetricById = (id: string): CatalogMetric | undefined => {
  const resolved = LEGACY_CUSTOM_INSIGHT_IDS[id] ?? id;
  return dynamicMetrics.get(resolved) ?? catalog.metrics.find((m) => m.id === resolved);
};

export const getTableMeta = (table: string | undefined): TableMeta | undefined =>
  table ? catalog.tables[table] : undefined;

export const getMetricsByCategory = (): { category: Category; metrics: CatalogMetric[] }[] => {
  const all = getAllMetrics();
  return catalog.categories
    .map((category) => ({ category, metrics: all.filter((m) => m.category === category.id) }))
    .filter((group) => group.metrics.length > 0);
};

export type AggregationOption = { fn: AggregationFn; label: string };

export const getAggregationOptionsForMetric = (
  metricId: string | undefined
): AggregationOption[] => {
  if (!metricId) {
    return [];
  }
  const metric = getMetricById(metricId);
  if (!metric) {
    return [];
  }
  const seen = new Set<AggregationFn>();
  const result: AggregationOption[] = [];
  metric.aggregations.forEach((agg) => {
    if (seen.has(agg.function)) {
      return;
    }
    seen.add(agg.function);
    result.push({
      fn: agg.function,
      label: catalog.aggregations[agg.function]?.label ?? agg.function,
    });
  });
  return result;
};

export const getDefaultAggregationForMetric = (
  metricId: string | undefined
): AggregationFn | undefined => getAggregationOptionsForMetric(metricId)[0]?.fn;

export type ColumnOption = { column: string; label: string };

// Columns intentionally hidden from the group-by picker even when the catalog
// lists them as groupable. The data warehouse supports grouping by these
// columns — we just don't want users to choose them in the UI (e.g. proactive
// sessions split charts in a way that's confusing more often than useful).
const GROUP_BY_UI_EXCLUDES: ReadonlySet<string> = new Set(['is_proactive']);

export const getGroupByOptionsForMetric = (metricId: string | undefined): ColumnOption[] => {
  if (!metricId) {
    return [];
  }
  const metric = getMetricById(metricId);
  if (!metric) {
    return [];
  }
  return (
    metric.groupBy
      .filter((key) => !GROUP_BY_UI_EXCLUDES.has(key))
      // The primary dimension defines the metric's identity (e.g. csat_brain for
      // AI CSAT score). It's silently included on save and should not appear as
      // a user-togglable option — unchecking it would either be a no-op or
      // ambiguously switch the metric to a sibling on the same CAGG.
      .filter((key) => key !== metric.primaryDimension)
      .map((key) => catalog.groupByOptions[key])
      .filter((opt): opt is GroupByOption => !!opt)
      .map((opt) => ({ column: opt.column, label: opt.label }))
  );
};

export const getGroupByLabel = (column: string | undefined): string | undefined => {
  if (!column) {
    return undefined;
  }
  return Object.values(catalog.groupByOptions).find((opt) => opt.column === column)?.label;
};

// Tuples that re-pointed metrics answered to before their re-point. A report
// saved without a metricId is identifiable only by its snapshot tuple; when a
// re-point removes that tuple from the catalog, the report would otherwise
// lose its identity entirely (label, derived formatting, healing). Maps each
// orphaned tuple to the metric ids that historically produced it — sibling
// ambiguity (ai_conversations vs ai_csat_brain, human_conversations vs
// human_csat) is resolved by primaryDimension, exactly like live collisions.
const LEGACY_METRIC_TUPLES: {
  table: string;
  function: AggregationFn;
  column?: string;
  metricIds: string[];
}[] = [
  {
    table: 'ai_agents_daily',
    function: 'sum',
    column: 'conv_count',
    metricIds: ['ai_conversations', 'ai_csat_brain'],
  },
  {
    table: 'ai_agents_daily',
    function: 'sum',
    column: 'meaningful_count',
    metricIds: ['ai_meaningful_conversations'],
  },
  {
    table: 'ai_agents_daily',
    function: 'sum',
    column: 'not_contained_count',
    metricIds: ['ai_containment'],
  },
  {
    table: 'human_sessions_daily',
    function: 'count',
    metricIds: ['human_conversations', 'human_csat'],
  },
];

export const findCatalogMetricFromQuery = (
  query: MetricsQueryRequest | undefined,
  metricId?: string
): { metric: CatalogMetric; aggregationFn: AggregationFn } | undefined => {
  if (!query) {
    return undefined;
  }
  const savedAgg = query.aggregations?.[0];
  // The saved function survives only while the metric still offers it. After a
  // re-point (sum → count_distinct) the snapshot's function no longer exists on
  // the metric; returning it would seed the report editor with a value absent
  // from the aggregation picker and make find-by-function save handlers
  // silently no-op — fall back to the metric's default instead.
  const resolveFn = (metric: CatalogMetric): AggregationFn => {
    const savedFn = savedAgg?.function;
    return savedFn && metric.aggregations.some((a) => a.function === savedFn)
      ? savedFn
      : (metric.aggregations[0]?.function as AggregationFn);
  };
  // When the report persists which catalog metric was chosen, trust it. This is
  // the only way to distinguish metrics that share (table, function, column,
  // primaryDimension) — e.g. the five custom-insight metrics on
  // ai_agents_custom_insights_daily, which only differ in defaultFilter.
  if (metricId) {
    const explicit = getMetricById(metricId);
    if (explicit) {
      return { metric: explicit, aggregationFn: resolveFn(explicit) };
    }
  }
  if (!savedAgg) {
    return undefined;
  }
  const live = getAllMetrics().filter(
    (m) =>
      m.table === query.table &&
      m.aggregations.some((a) => a.function === savedAgg.function && a.column === savedAgg.column)
  );
  // No live tuple match: the snapshot may predate a re-point that removed its
  // tuple from the catalog — resolve through the legacy tuple map instead.
  const candidates =
    live.length > 0
      ? live
      : (
          LEGACY_METRIC_TUPLES.find(
            (t) =>
              t.table === query.table &&
              t.function === savedAgg.function &&
              t.column === savedAgg.column
          )?.metricIds.map(getMetricById) ?? []
        ).filter((m): m is CatalogMetric => !!m);
  if (candidates.length === 0) {
    return undefined;
  }
  // When multiple metrics share the same (table, function, column) tuple
  // (e.g. ai_conversations and ai_csat_brain both = COUNT(DISTINCT session_id)
  // on ai_agents_conversations_daily), the saved query's groupBy tells them
  // apart: a metric's primaryDimension appears in the groupBy of any query that
  // meant to query that metric. Prefer that match; otherwise prefer the generic
  // metric (no primaryDimension); otherwise fall back to catalog order.
  const queryGroupBy = new Set(query.groupBy ?? []);
  const byPrimaryDimension = candidates.find(
    (m) => m.primaryDimension && queryGroupBy.has(m.primaryDimension)
  );
  const generic = candidates.find((m) => !m.primaryDimension);
  const metric = byPrimaryDimension ?? generic ?? candidates[0];
  return { metric, aggregationFn: resolveFn(metric) };
};

// Conjoins a catalog metric's defaultFilter (identity clause) with the user's
// saved filter at request build time, so saved reports stay clean of catalog-
// derived clauses but the API still receives the metric-scoping predicate.
// Returns undefined when neither side has content (avoids emitting `filter: ""`).
export const mergeFilters = (
  base: string | undefined,
  user: string | undefined
): string | undefined => {
  if (!base) {
    return user;
  }
  if (!user) {
    return base;
  }
  return `(${base}) and (${user})`;
};

export const getFilterOptionsForMetric = (
  metricId: string | undefined
): (ColumnOption & { type: FilterType })[] => {
  if (!metricId) {
    return [];
  }
  const metric = getMetricById(metricId);
  if (!metric) {
    return [];
  }
  return metric.filters
    .map((key) => catalog.filterOptions[key])
    .filter((opt): opt is FilterOption => !!opt)
    .map((opt) => ({ column: opt.column, label: opt.label, type: opt.type }));
};

// Older saved reports may only carry one aggregation (the historical first-of-function
// pick). For derived metrics we need both numerator and denominator, so re-attach
// any missing ones from the catalog when building requests.
const ensureDerivedAggregations = (
  aggregations: Aggregation[],
  metric: CatalogMetric
): Aggregation[] => {
  if (!metric.derived) {
    return aggregations;
  }
  const required = new Set([metric.derived.numerator, metric.derived.denominator]);
  const have = new Set(aggregations.map((a) => a.alias));
  const missing = metric.aggregations.filter((a) => required.has(a.alias) && !have.has(a.alias));
  return missing.length === 0 ? aggregations : [...aggregations, ...missing];
};

export type RequestSource = {
  table: string;
  aggregations: Aggregation[];
  groupBy?: string[];
  // The resolved catalog entry, so callers don't need a second
  // findCatalogMetricFromQuery pass for defaultFilter / derived / labels —
  // two independent lookups could drift and pair one metric's identity
  // filter with another's aggregations.
  metric?: CatalogMetric;
};

// Resolves the table, aggregations, and groupBy to send for a saved query. A
// report persists a snapshot of its metric's source at save time; when the
// catalog has since re-pointed the metric — to a different table (AI
// conversation metrics moved to a conversation-grain CAGG with
// COUNT(DISTINCT session_id) to stop double-counting router accounts) or to
// different aggregations on the same table (human metrics moved from count(*)
// to COUNT(DISTINCT session_id) on human_sessions_daily) — the snapshot is
// stale by definition. Trust the catalog and send its table + full aggregation
// set; never mix the old table with a new distinct count, which is a
// mixed-grain request the API rejects. This heals existing dashboards without
// a migration or a re-save.
//
// A snapshot is stale when its table moved or when any persisted aggregation
// is no longer offered by the metric. A user-chosen aggregation the metric
// still offers (e.g. avg over response_time) is by definition not stale and is
// kept, only re-attaching a derived metric's missing numerator/denominator.
//
// groupBy is filtered to the metric's current dimensions so a persisted column
// that has since become ungroupable (e.g. a column promoted to distinct key)
// doesn't fail the whole request.
export const resolveRequestSource = (
  query: MetricsQueryRequest,
  metricId?: string
): RequestSource => {
  const catalog = findCatalogMetricFromQuery(query, metricId);
  const snapshotAggs = query.aggregations ?? [];
  if (!catalog) {
    return { table: query.table, aggregations: snapshotAggs, groupBy: query.groupBy };
  }
  const { metric } = catalog;
  const stale =
    metric.table !== query.table ||
    snapshotAggs.some(
      (s) => !metric.aggregations.some((a) => a.function === s.function && a.column === s.column)
    );
  const groupBy = query.groupBy?.filter((column) => metric.groupBy.includes(column));
  if (stale) {
    return { table: metric.table, aggregations: metric.aggregations, groupBy, metric };
  }
  return {
    table: query.table,
    aggregations: ensureDerivedAggregations(snapshotAggs, metric),
    groupBy,
    metric,
  };
};
